import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DreamhubGradient } from "@dreamhub/designsystem/icons";
import IconContainer from "@/components/ui/icon-container";
import Text from "@/components/ui/text";
import UserAvatar from "@/components/controls/UserAvatar";
import { Popover, PopoverTrigger } from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import DreamerSourcesPopover from "@/components/controls/Task/DreamerSourcesPopover";
import { SourceEntity } from "@/components/controls/Task/useSourceActivity";
import {
  entityTypeToPrefix,
  EntityType
} from "@/services/hooks/entities/useEntityType";
import { getEntityActivityUrl } from "@/services/hooks/entities/getEntityActivityUrl";
import { TaskData } from "@/entities/tasks/types";
import { UserData } from "@/entities/users/types";
import { cn } from "@/utils/utils";

const TRANSLATION_KEY = "tasks.drawer.task";

interface LinkedEntity {
  entityType: EntityType;
  entityId: string;
}

const buildActivityUrl = (
  entity: SourceEntity,
  activityId: string
): string =>
  getEntityActivityUrl(entity.entityType, entity.entityId, activityId);

const getActivityLookupEntity = (
  entityIds: string[] | undefined
): LinkedEntity | undefined => {
  // Same priority as the "Linked to" column and edit modal: deal wins over
  // lead, falling back to company then person when neither is present.
  const priority = [
    EntityType.DEAL,
    EntityType.LEAD,
    EntityType.COMPANY,
    EntityType.PERSON
  ];
  for (const entityType of priority) {
    const prefix = entityTypeToPrefix[entityType];
    const entityId = prefix
      ? entityIds?.find((id) => id.startsWith(prefix))
      : undefined;
    if (entityId) return { entityType, entityId };
  }
  return undefined;
};

interface Props {
  task: TaskData;
  contextOwnerName?: string | null;
  contextOwnerUserData?: UserData | null;
  assigneeName?: string | null;
  compact?: boolean;
  // Chip text override for placements that already say "Created by".
  label?: string;
  // Hide the "for <user>" suffix where the surrounding column names the creator.
  hideForUserSuffix?: boolean;
}

const DREAMER_CHIP_CLASSES =
  "inline-flex items-center gap-1 p-[2px] pr-[6px] rounded-[10px] overflow-hidden bg-surface-badges-default border-[0.5px] border-[var(--grey-800)] w-fit whitespace-nowrap";
const DREAMER_ICON_CLASSES =
  "rounded-lg border-[0.5px] border-border-border1 backdrop-blur-[50px]";

interface ChipContentProps {
  label?: string;
}

const ChipContent: React.FC<ChipContentProps> = ({ label }) => {
  const { t } = useTranslation();
  return (
    <>
      <IconContainer size="5" className={DREAMER_ICON_CLASSES}>
        <DreamhubGradient />
      </IconContainer>
      <Text variant="smBodySecondary">
        {label ?? t(`${TRANSLATION_KEY}.createdByDreamer`)}
      </Text>
    </>
  );
};

interface ForUserSuffixProps {
  contextOwnerName: string;
  contextOwnerUserData?: UserData | null;
}

const ForUserSuffix: React.FC<ForUserSuffixProps> = ({
  contextOwnerName,
  contextOwnerUserData
}) => {
  const { t } = useTranslation();
  return (
    <div className="inline-flex items-center gap-1 whitespace-nowrap">
      <Text variant="smBodySecondary">
        {t(`${TRANSLATION_KEY}.dreamer.for`)}
      </Text>
      {contextOwnerUserData && (
        <UserAvatar userData={contextOwnerUserData} size="M" />
      )}
      <Text variant="smBodySecondary">{contextOwnerName}</Text>
    </div>
  );
};

const CreatedByDreamerIndication: React.FC<Props> = ({
  task,
  contextOwnerName,
  contextOwnerUserData,
  assigneeName,
  compact = false,
  label,
  hideForUserSuffix = false
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const sources = useMemo(
    () => task.dreamerMetadata?.sources ?? [],
    [task.dreamerMetadata]
  );

  const linkedEntity = useMemo(
    () => getActivityLookupEntity(task.entityIds),
    [task.entityIds]
  );

  const showForUserSuffix = Boolean(
    !hideForUserSuffix &&
      contextOwnerName &&
      task.createdBy &&
      task.assigneeId &&
      task.createdBy !== task.assigneeId
  );

  if (compact) {
    return (
      <div className="inline-flex items-center gap-2 w-fit">
        <div className={DREAMER_CHIP_CLASSES}>
          <ChipContent label={label} />
        </div>
        {showForUserSuffix && (
          <ForUserSuffix
            contextOwnerName={contextOwnerName!}
            contextOwnerUserData={contextOwnerUserData}
          />
        )}
      </div>
    );
  }

  // The navigation target comes from the source activity's own resolved
  // entity (passed in by the popover), not `linkedEntity` — the task's
  // linked record can differ from the record the activity actually lives on.
  const handleViewActivity = (activityId: string, entity: SourceEntity) => {
    navigate(buildActivityUrl(entity, activityId));
    setIsPopoverOpen(false);
  };

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              type="button"
              onClick={(event) => event.stopPropagation()}
              className={cn(
                "appearance-none bg-transparent p-0 border-0 cursor-pointer w-fit flex-shrink-0",
                "rounded-[10px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-field-focus",
                "transition-colors",
                "inline-flex items-center gap-1"
              )}
              aria-label={t(`${TRANSLATION_KEY}.dreamer.createdByDreamer`)}
            >
              <div
                className={cn(
                  DREAMER_CHIP_CLASSES,
                  "transition-all",
                  isPopoverOpen ? "brightness-125" : "hover:brightness-110"
                )}
              >
                <ChipContent label={label} />
              </div>
              {showForUserSuffix && (
                <ForUserSuffix
                  contextOwnerName={contextOwnerName!}
                  contextOwnerUserData={contextOwnerUserData}
                />
              )}
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          {t(`${TRANSLATION_KEY}.createdByDreamerTooltip`)}
        </TooltipContent>
      </Tooltip>
      <DreamerSourcesPopover
        task={task}
        sources={sources}
        entityType={linkedEntity?.entityType}
        entityId={linkedEntity?.entityId}
        contextOwnerName={contextOwnerName}
        assigneeName={assigneeName}
        onClose={() => setIsPopoverOpen(false)}
        onViewActivity={handleViewActivity}
      />
    </Popover>
  );
};

export default CreatedByDreamerIndication;
